import { createContext, useContext, ReactNode } from 'react';
import { Layer as MapLayer, LayerProps } from 'react-map-gl';
import { LAYER_ORDER_KEY, LAYER_ORDER } from './layer-order';

export { LAYER_ORDER };

const LayerBandContext = createContext<number | null>(null);

// react-map-gl's LayerProps type doesn't surface `metadata`, but MapLibre's
// addLayer accepts and stores it (and getStyle().layers returns it).
type OrderedLayerProps = LayerProps & { metadata?: Record<string, unknown> };

// Wrap a group of standard map layers so each `<Layer>` inside is tagged with
// this z-order band in its metadata. `useEnforceLayerOrder` reads that metadata
// to keep layers in a stable order regardless of toggle/mount timing.
export const LayerBand = ({
  band,
  children,
}: {
  band: number;
  children: ReactNode;
}) => (
  <LayerBandContext.Provider value={band}>{children}</LayerBandContext.Provider>
);

// Drop-in replacement for react-map-gl's `<Layer>`. Rendered inside a
// `<LayerBand>`, it stamps the band onto the layer's `metadata` so the central
// ordering hook manages it. Rendered outside a `<LayerBand>` it behaves exactly
// like the original `<Layer>` (untagged → never reordered).
export const Layer = (props: OrderedLayerProps) => {
  const band = useContext(LayerBandContext);
  if (band === null) return <MapLayer {...(props as LayerProps)} />;
  const tagged = {
    ...props,
    metadata: { ...props.metadata, [LAYER_ORDER_KEY]: band },
  };
  return <MapLayer {...(tagged as LayerProps)} />;
};
